import { useCallback, useEffect, useRef } from 'react'

import { useKnowledgeEditorContext } from 'apps/aiAutomation/components/KnowledgeEditor/lib/context'
import { SourceChunk } from 'apps/aiAutomation/components/Playground/types'

interface UseEditorScrollProps {
  highlightedChunks: Array<SourceChunk & { id: string }>
}

const TOP_OFFSET = 25

export const useEditorScroll = ({ highlightedChunks }: UseEditorScrollProps) => {
  const hasScrolledInitial = useRef(false)
  const isInternalScrolling = useRef(false)
  const editorWrapperRef = useRef<HTMLDivElement | null>(null)
  const { setSelectedChunkId, selectedChunkId } = useKnowledgeEditorContext()

  const scrollToHighlight = useCallback(
    (highlightId: string, withoutAnimation: boolean = false) => {
      const container = editorWrapperRef.current
      const targetNode = container?.querySelector<HTMLElement>(`span[data-anchor="${highlightId}"]`)

      if (!container || !targetNode) {
        return
      }

      isInternalScrolling.current = true
      setSelectedChunkId(highlightId)

      container.scrollTo({
        top: targetNode.offsetTop - TOP_OFFSET,
        behavior: withoutAnimation ? 'auto' : 'smooth',
      })
    },
    [setSelectedChunkId],
  )

  useEffect(() => {
    const container = editorWrapperRef.current
    if (!container) {
      return
    }

    const handleScrollEnd = () => {
      isInternalScrolling.current = false
    }

    container.addEventListener('scrollend', handleScrollEnd)

    return () => {
      container.removeEventListener('scrollend', handleScrollEnd)
    }
  }, [])

  useEffect(() => {
    const container = editorWrapperRef.current
    if (!container || highlightedChunks.length === 0) {
      return
    }

    const observerOptions = {
      root: container,
      threshold: 0.1,
    }

    const observer = new IntersectionObserver((entries) => {
      if (isInternalScrolling.current) {
        return
      }

      const intersectingEntry = entries.find((entry) => entry.isIntersecting)

      if (intersectingEntry) {
        const id = intersectingEntry.target.getAttribute('data-anchor')
        if (id && id !== selectedChunkId) {
          setSelectedChunkId(id)
        }
      } else {
        const leavingEntry = entries.find(
          (entry) =>
            !entry.isIntersecting && entry.target.getAttribute('data-anchor') === selectedChunkId,
        )

        if (leavingEntry) {
          setSelectedChunkId(null)
        }
      }
    }, observerOptions)

    const targets = container.querySelectorAll('span[data-anchor]')
    targets.forEach((target) => observer.observe(target))

    return () => observer.disconnect()
  }, [highlightedChunks, setSelectedChunkId, selectedChunkId])

  useEffect(() => {
    if (!highlightedChunks.length || hasScrolledInitial.current) {
      return
    }

    const initialId = selectedChunkId || highlightedChunks[0].id
    if (initialId) {
      requestAnimationFrame(() => {
        scrollToHighlight(initialId, true)
        hasScrolledInitial.current = true
      })
    }
  }, [highlightedChunks, scrollToHighlight, selectedChunkId])

  return {
    editorWrapperRef,
    onHighlightClick: scrollToHighlight,
  }
}
